import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { OgmaLogo } from "@/components/OgmaLogo";
import { Button } from "@/components/ui/Button";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { billingKeys } from "@/features/billing/query-keys";
import { useCheckoutSessionStatusQuery } from "@/features/billing/hooks/useBilling";
import { extractMessage } from "@/lib/errors";

const ACTIVATION_TIMEOUT_MS = 20_000;

export function CheckoutSuccessPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") ?? "";
  const queryClient = useQueryClient();
  const { data, error, isLoading } = useCheckoutSessionStatusQuery(sessionId);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (data?.complete && data.plan === "PAID") {
      queryClient.invalidateQueries({ queryKey: billingKeys.status() });
    }
  }, [data, queryClient]);

  const stillActivating = data?.complete && data.plan === "FREE";
  const [prevStillActivating, setPrevStillActivating] = useState(stillActivating);

  if (stillActivating !== prevStillActivating) {
    setPrevStillActivating(stillActivating);
    setTimedOut(false);
  }

  useEffect(() => {
    if (!stillActivating) return;
    const timer = setTimeout(() => setTimedOut(true), ACTIVATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [stillActivating]);

  const activated = data?.complete && data.plan === "PAID";
  const returnTo = activated && data.product === "SELF_HOSTED_PREMIUM" ? "/api-keys" : "/account";
  const returnLabel =
    activated && data.product === "SELF_HOSTED_PREMIUM"
      ? t("checkout.success.createApiKey")
      : t("checkout.success.backToAccount");

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-muted/40">
      <div className="flex w-96 flex-col items-center gap-6 rounded-xl border border-border bg-card px-10 py-12 text-center shadow-sm">
        <OgmaLogo size={40} />
        {renderStatus()}
        <Button className="w-full" render={<Link to={returnTo}>{returnLabel}</Link>} />
      </div>
    </div>
  );

  function renderStatus() {
    if (!sessionId || error) {
      return (
        <>
          <XCircle className="size-10 text-destructive" />
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold text-foreground">
              {t("checkout.success.notFoundTitle")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("checkout.success.notFoundDescription")}
            </p>
          </div>
          {error && <ErrorAlert message={extractMessage(error)} />}
        </>
      );
    }

    if (isLoading || !data?.complete) {
      return (
        <>
          <Loader2 className="size-10 animate-spin text-muted-foreground" />
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold text-foreground">
              {t("checkout.success.verifyingTitle")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("checkout.success.verifyingDescription")}
            </p>
          </div>
        </>
      );
    }

    if (data.plan === "FREE") {
      return (
        <>
          <Loader2 className="size-10 animate-spin text-muted-foreground" />
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold text-foreground">
              {t("checkout.success.confirmedTitle")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {timedOut
                ? t("checkout.success.timeoutDescription")
                : t("checkout.success.activating")}
            </p>
          </div>
          {timedOut && (
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              {t("checkout.success.refresh")}
            </Button>
          )}
        </>
      );
    }

    return (
      <>
        <CheckCircle2 className="size-10 text-primary" />
        <div className="flex flex-col gap-1">
          <h1 className="text-lg font-semibold text-foreground">
            {t("checkout.success.activatedTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {data.product === "SELF_HOSTED_PREMIUM"
              ? t("checkout.success.activatedSelfHosted")
              : t("checkout.success.activatedCloud")}
          </p>
        </div>
      </>
    );
  }
}
